import { useCallback, useRef } from 'react';
import { startRun } from '../apiClient/maps';

/**
 * Manages the server-authoritative run token for one PlayPage. Call
 * `requestToken(mapId)` the instant a run goes live (fire-and-forget —
 * it resolves during the lead-in, so it never blocks the Start→music
 * path) and read `getToken()` at submit time.
 *
 * Best-effort by design: if the server is unreachable (offline play) or
 * the request fails, the token stays `null` and the run still submits —
 * it saves as practice without leaderboard placement or trophies.
 */
export function useRunToken(): {
  requestToken: (mapId: string) => void;
  getToken: () => string | null;
} {
  const tokenRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);

  const requestToken = useCallback((mapId: string) => {
    tokenRef.current = null;
    // Tag each request so a stale in-flight fetch from a previous attempt
    // (e.g. quick restart) can't clobber the current run's token.
    const seq = (requestSeqRef.current += 1);
    void startRun(mapId)
      .then((token) => {
        if (requestSeqRef.current === seq) {
          tokenRef.current = token;
        }
      })
      .catch((err) => {
        console.warn('Failed to obtain run token (continuing without it):', err);
      });
  }, []);

  const getToken = useCallback(() => tokenRef.current, []);

  return { requestToken, getToken };
}
